<template>
  <div class="public-license-map w-100 h-100 position-relative">
    <GMap v-if="city" :city="city" :items="markers" :selected-marker-id="selectedLicenseId"
      disable-left-bar-toggle @deselect="selectLicense(null)">
      <template #marker="{ item: license }">
        <CustomMarker :options="{ position: license.latlng, title: formatAddress(license.data.address) }"
          @click.stop="selectLicense(license.id)">
          <button type="button" class="public-license-marker"
            :aria-label="`License ${license.data.licenseId}: ${formatAddress(license.data.address)}`"
            :aria-pressed="selectedLicenseId === license.id"
            :style="{ backgroundColor: markerColor(license.data.statusType) }" />
        </CustomMarker>
      </template>
    </GMap>
    <div v-if="loading || error || !markers.length" class="public-map-message bg-white rounded shadow-sm p-3" role="status">
      <template v-if="error">
        <div role="alert">Could not load the license map: {{ error.message }}</div>
        <button class="btn btn-link p-0 mt-2" @click="load">Retry</button>
      </template>
      <template v-else-if="loading">Loading licenses…</template>
      <template v-else>No public licenses with map locations.</template>
    </div>
    <section v-if="selected" class="public-license-detail bg-white rounded shadow p-3" aria-label="License details">
      <button type="button" class="btn-close float-end ms-3" aria-label="Close license details" @click="selectLicense(null)" />
      <div class="fw-semibold">{{ formatAddress(selected.data.address) }}</div>
      <div>License #{{ selected.data.licenseId }}</div>
      <div>{{ selected.data.statusLabel || selected.data.status }}</div>
      <div v-if="selected.data.licenseType">{{ selected.data.licenseType }}</div>
      <div v-if="selected.data.issuedOn">Issued: {{ selected.data.issuedOn }}</div>
      <div v-if="selected.data.expiresOn">Expires: {{ selected.data.expiresOn }}</div>
    </section>
  </div>
</template>

<script>
import { CustomMarker } from 'vue3-google-map';
import GMap from './common/GMap.vue';
import { PublicCity } from '@/models/PublicCity';
import { fetchApi } from '@/api';

export default {
  components: { GMap, CustomMarker },
  props: {
    cityId: String,
    selectedLicenseId: String,
    onlyShowActive: Boolean,
    onlyShowPending: Boolean
  },
  data: () => ({ city: null, licenses: [], loading: true, error: null, sequence: 0 }),
  computed: {
    markers() {
      return this.licenses.filter(license => {
        const type = license.data.statusType;
        if (this.onlyShowActive) return type === 'active';
        if (this.onlyShowPending) return type === 'pending';
        // Preserve the embedded map's default active/pending status filter.
        return type === 'active' || type === 'pending';
      });
    },
    selected() {
      return this.markers.find(license => license.id === this.selectedLicenseId);
    }
  },
  watch: {
    cityId: { immediate: true, handler() { this.load(); } }
  },
  beforeUnmount() { this.sequence++; },
  methods: {
    formatAddress(address) {
      return typeof address === 'string' ? address : address?.label ||
        [address?.streetNumber, address?.street, address?.unit].filter(Boolean).join(' ');
    },
    markerColor(type) {
      return type === 'active' ? '#33a852' : type === 'pending' ? '#ea4345' : '#6c757d';
    },
    selectLicense(id) {
      this.$router.push({
        name: id ? 'license-detail' : 'licenses',
        params: { cityId: this.cityId, ...(id ? { selectedLicenseId: id } : {}) },
        query: this.$route.query
      });
    },
    async load() {
      const sequence = ++this.sequence;
      this.city = null;
      this.licenses = [];
      this.loading = true;
      this.error = null;
      try {
        let after = null;
        do {
          const result = await fetchApi(`/v1/public/cities/${encodeURIComponent(this.cityId)}/licenses/map${
            after ? `?after=${encodeURIComponent(after)}` : ''
          }`);
          if (sequence !== this.sequence) return;
          if (result.city) this.city = new PublicCity(result.city);
          this.licenses.push(...result.licenses.map(license => ({ ...license, latlng: license.data.latlng })));
          after = result.nextCursor;
        } while (after);
      } catch (error) {
        if (sequence === this.sequence) {
          this.error = error;
          // Do not leave a partial or revoked public map visible after a page fails.
          this.city = null;
          this.licenses = [];
        }
      } finally {
        if (sequence === this.sequence) this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.public-license-map { min-height: 240px; }
.public-map-message { position: absolute; top: 12px; left: 12px; max-width: calc(100% - 24px); }
.public-license-detail { position: absolute; bottom: 24px; left: 12px; max-width: min(360px, calc(100% - 24px)); }
.public-license-marker {
  display: block; width: 18px; height: 18px; padding: 0; border: 2px solid white;
  border-radius: 50%; box-shadow: 0 0 0 2px rgba(0, 0, 0, .2); cursor: pointer;
}
.public-license-marker:hover, .public-license-marker:focus, .public-license-marker[aria-pressed="true"] {
  transform: scale(1.5);
}
</style>
